/**
 * Repo sync attention notice.
 *
 * Derives the one line the wiki sidebar header shows about repo sync, and only
 * when the user has something to look at: a sync in flight, a sync PR to open,
 * or a failure to fix. Every quiet state (no run yet, nothing changed, skipped,
 * or simply synced a while ago) returns `null` so the header stays empty.
 */

import type { WikiSettingResponse } from '@/lib/wiki-settings/schema';

import { WikiRepoSyncRunStatus } from '@/generated/prisma/enums';

export type LatestRepoSyncRun = NonNullable<WikiSettingResponse['latestRepoSyncRun']>;

export type RepoSyncNotice = {
  text: string;
  /** Full message shown on hover when the sidebar-width text is abbreviated. */
  tooltip?: string;
  href?: string;
  isError?: boolean;
};

export type RepoSyncNoticeArgs = {
  installationId: number | null;
  owner?: string | null;
  repo?: string | null;
};

function getGitHubInstallationSettingsUrl(installationId: number | null) {
  return installationId
    ? `https://github.com/settings/installations/${installationId}`
    : 'https://github.com/settings/installations';
}

export function getWikiRepoSyncNotice(
  run: LatestRepoSyncRun | null,
  args: RepoSyncNoticeArgs,
): RepoSyncNotice | null {
  if (run?.status === WikiRepoSyncRunStatus.pending) {
    return { text: 'Sync in progress' };
  }

  // Failure texts are kept short so they fit the narrow sidebar column; the
  // tooltip carries the full message.
  if (run?.status === WikiRepoSyncRunStatus.failed) {
    switch (run.errorClass) {
      case 'permission_denied':
        return {
          text: 'Sync failed: app permissions',
          tooltip: 'Sync failed: update GitHub App permissions',
          href: getGitHubInstallationSettingsUrl(args.installationId),
          isError: true,
        };
      case 'target_conflict':
        return {
          text: 'Sync failed: directory in use',
          tooltip: 'Sync failed: choose an empty directory',
          isError: true,
        };
      case 'repo_unavailable':
        return {
          text: 'Sync failed: repo unavailable',
          tooltip: 'Sync failed: repository unavailable',
          isError: true,
        };
      case 'size_exceeded':
        return {
          text: 'Sync failed: export too large',
          tooltip: 'Sync failed: wiki export is too large',
          isError: true,
        };
      case 'push_rejected':
        return {
          text: 'Sync failed: push rejected',
          tooltip: 'Sync failed: branch update rejected',
          isError: true,
        };
      default:
        return { text: 'Sync failed: try again', isError: true };
    }
  }

  if (
    run?.status === WikiRepoSyncRunStatus.pr_opened ||
    run?.status === WikiRepoSyncRunStatus.pr_updated
  ) {
    const prUrl =
      run.prNumber && args.owner && args.repo
        ? `https://github.com/${encodeURIComponent(args.owner)}/${encodeURIComponent(args.repo)}/pull/${run.prNumber}`
        : null;
    const versionSuffix = run.wikiVersion ? ` for v${run.wikiVersion}` : '';
    const verb = run.status === WikiRepoSyncRunStatus.pr_opened ? 'opened' : 'updated';

    return {
      text: run.prNumber ? `PR #${run.prNumber} ${verb}${versionSuffix}` : `Sync PR ${verb}`,
      href: prUrl ?? undefined,
    };
  }

  // Quiet: no run, no_changes, skipped, or an older completed sync.
  return null;
}
